/**
 * Login Component — JAD Enterprises branded
 * Includes forgot password flow
 */

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ensureCurrentVersion } from '../../services/versionGate';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const { signIn, resetPassword, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signIn(email, password);
      // Version gate: if a deploy happened while the user was on the login
      // screen (or they're on a stale-cached tab), force a hard reload now
      // — login is a natural latency moment and gives users a reliable
      // recovery path ("sign out and back in") for stuck-stale state.
      // If the gate triggers, it calls window.location.reload() and this
      // line never resolves; the navigate() below only runs when versions
      // already match.
      const reloading = await ensureCurrentVersion();
      if (reloading) return;
      navigate('/');
    } catch {
      // Error handled by auth context
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotMessage(null);
    try {
      await resetPassword(forgotEmail);
      setForgotMessage('Password reset email sent. Check your inbox.');
    } catch (err) {
      setForgotMessage('Error: ' + (err instanceof Error ? err.message : 'Failed to send'));
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/logo.png" alt="JAD Enterprises" className="login-logo" />
        <h1>AI Calling Portal</h1>
        <p className="login-subtitle">
          {showForgot ? 'Reset your password' : 'Sign in to manage your campaigns'}
        </p>

        {error && !showForgot && <div className="error-message">{error}</div>}
        {forgotMessage && (
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.85rem',
            background: forgotMessage.startsWith('Error') ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
            border: `1px solid ${forgotMessage.startsWith('Error') ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`,
            color: forgotMessage.startsWith('Error') ? '#dc2626' : '#10b981',
          }}>{forgotMessage}</div>
        )}

        {!showForgot ? (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                required disabled={loading} placeholder="your@email.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password" type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                required disabled={loading} placeholder="Enter password"
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button type="button" onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotMessage(null); }}
                style={{ background: 'none', border: 'none', color: 'var(--gold)', cursor: 'pointer', fontSize: '0.85rem', fontFamily: 'inherit' }}>
                Forgot Password?
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleForgotPassword}>
            <div className="form-group">
              <label htmlFor="forgot-email">Email Address</label>
              <input
                id="forgot-email" type="email" value={forgotEmail}
                onChange={e => setForgotEmail(e.target.value)}
                required placeholder="your@email.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginBottom: '10px' }}>
              Send Reset Link
            </button>
            <button type="button" onClick={() => { setShowForgot(false); setForgotMessage(null); }}
              className="btn btn-secondary" style={{ width: '100%' }}>
              Back to Sign In
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
